import React, { useCallback } from 'react';
import { makeLogger } from '@/utils/logger/logger';
import { AssistantResponseData } from '@/types/Document.ts';
import ChatQuery from './ChatQuery.tsx';
import { DocumentWithDownloadUrl as Document, PendingOption } from '@/client';
import { AssistantQuery } from '../types.ts';
import { isQueryExecuting } from '@/components/dashbaord/assistant/queryUtil.ts';
import { formatContractAnswer } from './citationUtils.ts';
import { VStack } from '@chakra-ui/react';
import ChatContainerResponse from './ChatContainerResponse.tsx';
import RecordEditDrawer from '@/components/documents/RecordEditDrawer.tsx';
import { useCitationNavigation } from '@/hooks/assistant/useCitationNavigation.ts';

const logger = makeLogger('chat-container');

interface ChatContainerProps {
	documents: Document[];
	query: AssistantQuery;
	chatMode: boolean;
	showCitationPills: boolean;
	/** Sends a pending-action option's reply as the next message. */
	onSubmitPendingReply: (text: string) => void;
}

const ChatContainerComponent: React.FC<ChatContainerProps> = (props: ChatContainerProps) => {
	const { query, chatMode, documents, showCitationPills, onSubmitPendingReply } = props;
	const isLoading = isQueryExecuting(query);
	logger.debug('query.chunkRefs:', query.chunkRefs, 'status:', query.status);
	const response: AssistantResponseData | null = query.response ?? null;
	const responseText = response?.text || '';

	const {
		highlightedAnnotationIndex,
		sourcesOpen,
		drawerDocument,
		drawerCitationPages,
		registerAnnotationRef,
		handleCitationClick,
		openDocumentDrawer,
		handleSourcesOpenChange,
		handleSourcesAnimationStart,
		closeDrawerDocument,
	} = useCitationNavigation({ documents, sources: query.sources });

	// Moves trailing citations after the full stop and tags each [n] marker with its citation
	// index. See citationUtils for the pieces + tests. Memoized so ChatResponseText can keep the
	// parsed markdown stable across pill-click re-renders.
	const parseResponseTextWithLinks = useCallback(
		(text: string): string => formatContractAnswer(text, query.sources ?? [], isLoading),
		[query.sources, isLoading],
	);

	const handleSelectPendingOption = useCallback(
		(option: PendingOption) => onSubmitPendingReply(option.reply),
		[onSubmitPendingReply],
	);

	return (
		<VStack minW="100%" w="100%" gap={4} align="stretch" className="chat-container">
			{query && <ChatQuery query={query} />}
			{drawerDocument && (
				<RecordEditDrawer
					document={drawerDocument}
					citedPages={drawerCitationPages}
					isOpen
					hideFooter
					onOpenChange={(state) => {
						if (!state.open) closeDrawerDocument();
					}}
				/>
			)}
			<ChatContainerResponse
				isLoading={isLoading}
				responseText={responseText}
				chatMode={chatMode}
				response={response}
				documents={documents}
				parseResponseTextWithLinks={parseResponseTextWithLinks}
				highlightedAnnotationIndex={highlightedAnnotationIndex}
				exchangeId={query.queryId ?? null}
				sources={query.sources}
				citations={query.citations}
				showCitationPills={showCitationPills}
				registerAnnotationRef={registerAnnotationRef}
				onCitationClick={handleCitationClick}
				onOpenDocument={openDocumentDrawer}
				sourcesOpen={sourcesOpen}
				onSourcesOpenChange={handleSourcesOpenChange}
				onSourcesAnimationStart={handleSourcesAnimationStart}
				webSources={query.webSources}
				appliedCorrections={query.appliedCorrections}
				pendingAction={query.pendingAction}
				offeredAction={query.offeredAction}
				offerOutcome={query.offerOutcome}
				statusLabel={query.statusLabel}
				onSubmitPendingReply={handleSelectPendingOption}
			/>
		</VStack>
	);
};

// Memoized so completed exchanges skip re-rendering on every streamed chunk of a newer exchange.
// Don't add broad context reads (e.g. useAssistant) inside this subtree: that context value changes
// per chunk and would defeat the memo — display settings like showCitationPills arrive as props.
export default React.memo(ChatContainerComponent);
